import { useState } from 'react';
import { Link, useLocation, useNavigate, } from 'react-router-dom';
import {useDispatch, useSelector, } from 'react-redux';
import { userLogOut, userClearLogOut,} from '../../../hooks/local/userReducer';
import Modal from '../../../components/modal';
import logout from '../../../assets/svg-icons/logoutIcon.svg';
import resources from '../../../assets/svg-icons/resources.svg';
import manage from '../../../assets/svg-icons/edit.svg';
import billing from '../../../assets/svg-icons/blackBilling.svg';
import support from '../../../assets/svg-icons/blackSupport.svg';
import setting from '../../../assets/svg-icons/blackSetting.svg';
import arrow from '../../../assets/svg-icons/blueArrow.svg';

import { Database, Globe, Handshake, Logs, ServerCrash, FileLock2, PowerCircleIcon, Monitor,
} from 'lucide-react';

// ============================================================
// SIDEBAR DROPDOWN
// ============================================================

const SidebarDropdown = ({ item, isOpen, toggleDropdown, activeSidebarItem, idleSidebarItem, location,
}) => {
  const isActive =
    item.sublinks?.some(
      (sublink) =>
        location.pathname ===
          sublink.path ||
        (
          sublink.path !==
            '/client-area' &&
          location.pathname.startsWith(
            `${sublink.path}/`,
          )
        ),
    );

  return (
    <div className="grid">

      {/* DROPDOWN HEADER */}

      <div
        className={`${
          isActive
            ? activeSidebarItem
            : idleSidebarItem
        } cursor-pointer`}
        onClick={() =>
          toggleDropdown(
            item.label,
          )
        }
      >

        {item.iconComponent ? (

          <div className="h-4 w-4">
            {item.iconComponent}
          </div>

        ) : (

          <img
            src={item.icon}
            alt=""
            className="h-4"
          />

        )}

        <div className="text-center truncate">
          {item.label}
        </div>

        {item.sublinks && (

          <img
            src={arrow}
            alt=""
            className={`h-8 ml-auto transition-transform ${
              isOpen
                ? 'rotate-90'
                : ''
            }`}
          />

        )}

      </div>

      {/* SUB LINKS */}

      {item.sublinks &&
        isOpen && (

        <div className="ml-6 space-y-[1px] grid">

          {item.sublinks.map(
            (
              sublink,
              index,
            ) => {

              const sublinkActive =
                location.pathname ===
                  sublink.path ||
                (
                  sublink.path !==
                    '/client-area' &&
                  location.pathname.startsWith(
                    `${sublink.path}/`,
                  )
                );

              return (
                <Link
                  key={index}
                  to={
                    sublink.path
                  }
                  className="grid"
                >

                  <div
                    className={`${
                      sublinkActive
                        ? activeSidebarItem
                        : idleSidebarItem
                    } pl-4`}
                  >

                    <div className="text-center truncate">
                      {
                        sublink.label
                      }
                    </div>

                  </div>

                </Link>
              );
            },
          )}

        </div>

      )}

    </div>
  );
};

// ============================================================
// SIDEBAR
// ============================================================

const SideBar = () => {
  const location =
    useLocation();

  const navigate =
    useNavigate();

  const dispatch =
    useDispatch();

  // ============================================================
  // CURRENT PROJECT
  // ============================================================

  const selectedProject =
    useSelector(
      (state) =>
        state.user
          .selectedProject,
    );

  const projectId =
    selectedProject?.id;

  /*
   * Endpoint Monitoring is now
   * PROJECT scoped.
   *
   * If a project is not currently
   * selected, clicking it will
   * return the user to Resources.
   */
  const endpointMonitoringPath =
    projectId
      ? `/resources/${projectId}/endpoint-monitoring`
      : '/client-area';

  // ============================================================
  // SIDEBAR STYLES
  // ============================================================

  const activeSidebarItem =
    'flex items-center gap-3 bg-primaryBlue/20 text-primaryBlue p-4 rounded-md text-xs';

  const idleSidebarItem =
    'flex items-center gap-3 text-primaryBlue p-4 rounded-md text-xs hover:bg-primaryBlue/5';

  // ============================================================
  // STATE
  // ============================================================

  const [
    showLogoutModal,
    setShowLogoutModal,
  ] = useState(false);

  const [
    dropdownStates,
    setDropdownStates,
  ] = useState({});

  // ============================================================
  // LOGOUT
  // ============================================================

  const handleLogout = () => {
    setShowLogoutModal(
      true,
    );
  };

  const confirmLogout =
    async () => {
      try {
        await dispatch(
          userLogOut(),
        ).unwrap();
      } catch (error) {
        console.error(
          'Logout request failed:',
          error,
        );
      } finally {
        dispatch(
          userClearLogOut(),
        );

        navigate(
          '/sign-in',
          {
            replace: true,
          },
        );
      }
    };

  // ============================================================
  // DROPDOWN
  // ============================================================

  const toggleDropdown = (
    label,
  ) => {
    setDropdownStates(
      (prev) => ({
        ...prev,

        [label]:
          !prev[label],
      }),
    );
  };

  // ============================================================
  // CHECK ACTIVE SIDEBAR ITEM
  // ============================================================

  const isSidebarItemActive =
    (item) => {
      if (!item?.path) {
        return false;
      }

      /*
       * Exact match.
       */
      if (
        location.pathname ===
        item.path
      ) {
        return true;
      }

      /*
       * Allows pages underneath
       * the main route to keep the
       * sidebar item active.
       *
       * Example:
       *
       * /resources/123/endpoint-monitoring
       *
       * remains active when visiting:
       *
       * /resources/123/endpoint-monitoring/5
       */
      if (
        item.path !==
          '/client-area' &&
        location.pathname.startsWith(
          `${item.path}/`,
        )
      ) {
        return true;
      }

      return false;
    };

  // ============================================================
  // MAIN RESOURCE NAVIGATION
  // ============================================================

  const mainItems = [
    {
      path: '/client-area',

      label: 'Resources',

      icon: resources,
    },

    {
      path:
        '/client-area/manage',

      label:
        'Manage project',

      icon: manage,
    },

    {
      path:
        '/manageServerSnapshots',

      label:
        'Server snapshots',

      iconComponent: (
        <ServerCrash className="text-primaryBlue h-4 w-4" />
      ),
    },
  ];

  // ============================================================
  // INFRASTRUCTURE / SERVICE NAVIGATION
  // ============================================================

  const serviceItems = [
    // ========================================================
    // VOLUMES
    // ========================================================

    {
      label: 'Volumes',

      iconComponent: (
        <Database className="text-primaryBlue h-4 w-4" />
      ),

      sublinks: [
        {
          path:
            '/client-area/volumes',

          label: 'Volumes',
        },

        {
          path:
            '/manageVolumeSnapshots',

          label:
            'Volume Snapshots',
        },

        {
          path:
            '/manageVolumeBackups',

          label:
            'Volume Backups',
        },
      ],
    },

    // ========================================================
    // NETWORKS
    // ========================================================

    {
      label: 'Networks',

      iconComponent: (
        <Globe className="text-primaryBlue h-4 w-4" />
      ),

      sublinks: [
        {
          path: '/networks',

          label:
            'Private Networks',
        },

        {
          path:
            '/securityGroups',

          label:
            'Security groups',
        },

        {
          path: '/routers',

          label: 'Routers',
        },

        {
          path:
            '/floating-ips',

          label:
            'Floating IPs',
        },

        {
          path:
            '/manageLoadBalancers',

          label:
            'Load Balancers',
        },

        {
          path:
            '/networks/vpn',

          label: 'VPN',
        },
      ],
    },

    // // ========================================================
    // // ENDPOINT MONITORING
    // // ========================================================

    // {
    //   path:
    //     endpointMonitoringPath,

    //   label:
    //     'Endpoint Monitoring',

    //   iconComponent: (
    //     <Monitor className="text-primaryBlue h-4 w-4" />
    //   ),
    // },

    // ========================================================
    // CERTIFICATE MANAGER
    // ========================================================

    {
      path:
        '/certificate-manager',

      label:
        'Certificate Manager',

      iconComponent: (
        <FileLock2 className="text-primaryBlue h-4 w-4" />
      ),
    },

    // ========================================================
    // BILLING
    // ========================================================

    {
      path: '/billing',

      label: 'Billing',

      icon: billing,
    },

    // ========================================================
    // SUPPORT
    // ========================================================

    {
      path: '/help',

      label: 'Support',

      icon: support,
    },

    // ========================================================
    // PAYMENT DISPUTE
    // ========================================================

    {
      path: '/help',

      label:
        'Payment dispute',

      iconComponent: (
        <Handshake className="text-primaryBlue h-4 w-4" />
      ),

      onClick: () =>
        sessionStorage.setItem(
          'supportType',
          'payment-dispute',
        ),
    },

    // ========================================================
    // EVENT LOGS
    // ========================================================

    {
      path:
        '/event-logs',

      label: 'Event Logs',

      iconComponent: (
        <Logs className="text-primaryBlue h-4 w-4" />
      ),
    },
  ];

  // ============================================================
  // RENDER SIDEBAR ITEM
  // ============================================================

  const renderSidebarItem = (
    item,
    index,
  ) => {
    /*
     * Dropdown menu
     */
    if (item.sublinks) {
      return (
        <SidebarDropdown
          key={index}
          item={item}
          isOpen={
            dropdownStates[
              item.label
            ] || false
          }
          toggleDropdown={
            toggleDropdown
          }
          activeSidebarItem={
            activeSidebarItem
          }
          idleSidebarItem={
            idleSidebarItem
          }
          location={
            location
          }
        />
      );
    }

    /*
     * Normal menu
     */
    const isActive =
      isSidebarItemActive(
        item,
      );

    return (
      <Link
        key={index}
        to={item.path}
        className="grid"
        onClick={
          item.onClick
        }
      >

        <div
          className={
            isActive
              ? activeSidebarItem
              : idleSidebarItem
          }
        >

          {item.iconComponent ? (

            <div className="h-4 w-4">
              {
                item.iconComponent
              }
            </div>

          ) : (

            <img
              src={item.icon}
              alt=""
              className="h-4"
            />

          )}

          <div className="text-center truncate">
            {item.label}
          </div>

        </div>

      </Link>
    );
  };

  // ============================================================
  // SIDEBAR UI
  // ============================================================

  return (
    <div className="lg:col-span-2 bg-white dark:bg-[#141D26] border-r dark:border-r-[#c4c4c432] dark:text-white hidden lg:flex min-h-screen h-[100vh] sticky top-0 left-0 overflow-y-auto flex-col">

      <div className="px-4 h-full flex flex-col">

        <div className="py-3 flex flex-col h-full">

          {/* ================================================= */}
          {/* MAIN ITEMS */}
          {/* ================================================= */}

          <div className="space-y-[1px] grid">

            {mainItems.map(
              renderSidebarItem,
            )}

          </div>

          {/* ================================================= */}
          {/* SERVICE ITEMS */}
          {/* ================================================= */}

          <div className="space-y-[1px] border-y border-[#c4c4c432] grid py-2 mt-2">

            {serviceItems.map(
              renderSidebarItem,
            )}

          </div>

          {/* ================================================= */}
          {/* ACCOUNT ACTIONS */}
          {/* ================================================= */}

          <div className="space-y-[1px] grid py-2">

            {/* SETTINGS */}

            <Link
              to="/settings"
              className="grid"
            >

              <div
                className={
                  location.pathname ===
                  '/settings'
                    ? activeSidebarItem
                    : idleSidebarItem
                }
              >

                <img
                  src={setting}
                  alt=""
                  className="h-4"
                />

                <div className="text-center truncate">
                  Settings
                </div>

              </div>

            </Link>

            {/* SIGN OUT */}

            <button
              type="button"
              onClick={
                handleLogout
              }
              className="flex w-full gap-3 items-center text-primaryBlue p-4 rounded-md text-xs hover:bg-primaryBlue/5"
            >

              <img
                src={logout}
                alt=""
                className="h-4"
              />

              <span className="text-xs text-primaryBlue">
                Sign out
              </span>

            </button>

          </div>

          {/* ================================================= */}
          {/* LOGOUT MODAL */}
          {/* ================================================= */}

          <Modal
            title="Sign Out"
            isVisible={
              showLogoutModal
            }
            onClose={() =>
              setShowLogoutModal(
                false,
              )
            }
            size="md:w-[25%]"
            className="!z-[9999]"
          >

            <div className="flex flex-col items-center text-center p-2">

              <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-full mb-4">

                <PowerCircleIcon className="size-8 text-red-500" />

              </div>

              <h3 className="text-lg font-semibold mb-2 text-red-500">
                Confirm Sign Out
              </h3>

              <p className="text-gray-500 dark:text-gray-400 mb-8 text-sm">
                Are you sure you
                want to sign out?
                You will need to
                sign in again to
                access your
                resources.
              </p>

              <div className="grid gap-3 w-full mt-2">

                <button
                  type="button"
                  onClick={
                    confirmLogout
                  }
                  className="px-5 py-2.5 rounded text-sm font-medium bg-red-500 text-white hover:bg-red-600 transition-all"
                >
                  Sign Out
                </button>

                <button
                  type="button"
                  onClick={() =>
                    setShowLogoutModal(
                      false,
                    )
                  }
                  className="px-5 py-2.5 rounded text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-slate-700 dark:text-gray-200 transition-colors"
                >
                  Cancel
                </button>

              </div>

            </div>

          </Modal>

        </div>

      </div>

    </div>
  );
};

export default SideBar;